import { Ref, RefObject, useEffect, useMemo, useState } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';
import type { VirtualItem } from '@tanstack/react-virtual';

import { debounce } from 'utils/debounce';

import { LANDING_PAGES_CONTAINER_ID } from './components/Grid';

export type PageVirtualized = {
  measureElement: Ref<HTMLDivElement>;
  rendered: boolean;
  height: number;
};

type Props = {
  count: number;
  getContentRef: () => RefObject<HTMLDivElement>;
  children: (props: { getPageVirtualized?: (i: number) => PageVirtualized }) => JSX.Element | null;
  getRerenderDeps?: (args: { visibleItems: VirtualItem[] }) => any[];
};

export function Virtualizer(props: Props) {
  const { count, getContentRef, children, getRerenderDeps = () => [] } = props;

  const [initialized, setInitialized] = useState(false);

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => getContentRef().current,
    estimateSize: () => 1,
    scrollToFn: () => {},
    overscan: 1,
    measureElement: (element, entry, instance) => {
      if (entry?.borderBoxSize) {
        const box = entry.borderBoxSize[0];
        if (box) {
          const size = box[instance.options.horizontal ? 'inlineSize' : 'blockSize'];
          return size;
        }
      }
      return element.getBoundingClientRect()[instance.options.horizontal ? 'width' : 'height'];
    },
  });

  const items = virtualizer.getVirtualItems();

  const remeasureVisible = debounce(() => {
    Array.from(virtualizer.measureElementCache.entries())
      .filter((_, i) => items.some((item) => item.index === i))
      .forEach(([, e]) => virtualizer.measureElement(e));
  }, 200);

  const getPageVirtualized = (i: number) => ({
    measureElement: virtualizer.measureElement,
    rendered: !initialized || items.some((item) => item.index === i),
    height: virtualizer.measurementsCache[i]?.size,
  });

  const rerenderDeps = [
    initialized,
    initialized && items[0]?.index,
    initialized && items[items.length - 1]?.index,
    initialized && Math.floor(virtualizer.getTotalSize()),
  ];

  const itemsSerialized = items.map((i) => i.index).join(',');
  const externalRerenderDeps = useMemo(
    () => getRerenderDeps({ visibleItems: items }),
    [itemsSerialized, getRerenderDeps],
  );

  useEffect(() => {
    if (initialized) return;
    setTimeout(() => {
      virtualizer.measureElementCache.forEach(virtualizer.measureElement);
      setInitialized(true);
    }, 0);
  }, [initialized]);

  // Need to show all elements for a repeat measurment when resizing the window
  useEffect(() => {
    if (!initialized) return;
    remeasureVisible();
  }, [Math.floor(virtualizer.getTotalSize())]);

  const memoizedChildren = useMemo(
    () => children({ getPageVirtualized }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...rerenderDeps, ...externalRerenderDeps],
  );

  return memoizedChildren;
}

export function MaybeVirtualizer(props: { getEnabled: () => boolean } & Props) {
  const { getEnabled, ...rest } = props;
  const [, setInit] = useState(false);
  const virtualize = getEnabled();
  const Component = useMemo(
    () => (virtualize ? Virtualizer : (p: { children: Props['children'] }) => p.children({})),
    [virtualize],
  );
  useEffect(() => setInit(true), []);
  useEffect(() => {
    if (virtualize) {
      document.getElementById(LANDING_PAGES_CONTAINER_ID)?.setAttribute('data-virtualized', 'true');
    }
    return () => {
      document.getElementById(LANDING_PAGES_CONTAINER_ID)?.removeAttribute('data-virtualized');
    };
  }, [virtualize]);
  return <Component {...rest} />;
}
